import { dssChainData, dssConfigFor, dssDebtToken } from '@1delta/data-sdk'
import { parseRawAmount } from '../../../utils/parsing'
import { isFailedCall } from '../../../utils/multicall'
import { createMarketUid } from '../../../utils'
import { getDisplayPrice, getOraclePrice } from '../utils/oraclePrice'
import { createMultiAccountTypeUserState } from '../utils'
import {
  LenderCrossPoolMeta,
  LenderToLenderCrossPoolMeta,
  UserData,
} from '../utils/types'
import { dssLenderKey } from '../../public-data/dss/convertPublic'
import { getCachedDssCdps, DSS_CALLS_PER_CDP } from './userCallBuild'

/** Per-CDP position detail attached to the debt row (raw strings). */
export interface DssPositionInfo {
  /** DssCdpManager id — the sub-account id and every write op's target. */
  cdpId: string
  /** Urn handle in the Vat. */
  urn: string
  ilk: string
}

const RAY = 10n ** 27n

const big = (v: any): bigint => {
  try {
    if (typeof v === 'bigint') return v
    if (typeof v === 'number' || typeof v === 'string') return BigInt(v)
  } catch {}
  return 0n
}

/** Field access for decoded tuples that may arrive named or positional. */
const field = (res: any, name: string, idx: number): bigint =>
  big(res?.[name] ?? res?.[idx])

/**
 * Returns a [converter, expectedCallCount] tuple for dss user data (Sky and
 * the USDD fork alike).
 *
 * Layout mirrors `buildDssUserCall`'s phase-3 output, sliced via the
 * discovery cache (Teller pattern): per discovered CDP one `Vat.urns` read,
 * then one `Vat.ilks` per unique ilk at the tail. Emits one `UserData` per
 * market key `<BRAND>_<chainId>_<ILK>`, with ONE SUB-ACCOUNT PER CDP ID (a user
 * can hold many CDPs on the same ilk). Debt is `art × rate` (wad × ray →
 * wad), so it accrues between reads without any per-CDP storage write —
 * exactly Maker's accounting.
 */
export const getDssUserDataConverter = (
  lender: string,
  chainId: string,
  account: string,
  meta?: LenderToLenderCrossPoolMeta,
): [(data: any[]) => { [lender: string]: UserData } | undefined, number] => {
  const cfg = dssConfigFor(lender, chainId)
  const markets = dssChainData(lender, chainId)?.markets ?? []
  const discovery = getCachedDssCdps(lender, chainId, account)
  const cdps = discovery?.cdps ?? []
  const ilks = discovery?.ilks ?? []
  const expected =
    cfg && markets.length > 0 && cdps.length > 0
      ? cdps.length * DSS_CALLS_PER_CDP + ilks.length
      : 0

  return [
    (data: any[]) => {
      if (!cfg || expected === 0) return undefined
      if (!data || data.length < expected) return undefined

      // Tolerant reader — `usdd` on the published USDD config, `debtToken`
      // on Sky's.
      const rawDebtAddr = dssDebtToken(cfg)
      if (!rawDebtAddr) return undefined
      const debtAddr = rawDebtAddr.toLowerCase()
      const debtDecimals = cfg.debtDecimals ?? 18

      // Tail — the per-ilk rate legs.
      const rateByIlk = new Map<string, bigint>()
      ilks.forEach((ilk, i) => {
        const res = data[cdps.length * DSS_CALLS_PER_CDP + i]
        if (!isFailedCall(res)) rateByIlk.set(ilk, field(res, 'rate', 1))
      })

      // Group positions per market key (ilk), sub-account per cdpId.
      const perMarket = new Map<
        string,
        {
          market: (typeof markets)[number]
          positions: { cdp: DssPositionInfo; ink: bigint; debt: bigint }[]
        }
      >()

      cdps.forEach((cdp, i) => {
        const res = data[i * DSS_CALLS_PER_CDP]
        // A failed read is not an empty CDP — skip rather than reporting
        // the position as closed.
        if (isFailedCall(res)) return
        const ink = field(res, 'ink', 0)
        const art = field(res, 'art', 1)
        const rate = rateByIlk.get(cdp.ilk) ?? RAY
        const debt = (art * rate) / RAY
        if (ink === 0n && debt === 0n) return
        const market = markets.find((m) => m.ilk === cdp.ilk)
        if (!market) return
        const key = dssLenderKey(lender, chainId, cdp.ilk)
        if (!perMarket.has(key)) perMarket.set(key, { market, positions: [] })
        perMarket.get(key)!.positions.push({ cdp, ink, debt })
      })

      const out: { [lender: string]: UserData } = {}

      for (const [lenderKey, { market, positions }] of perMarket) {
        const metaMap: LenderCrossPoolMeta | undefined = meta?.[lenderKey]
        if (!metaMap) continue

        const collAddr = market.collToken.toLowerCase()
        const collUid = createMarketUid(chainId, lenderKey, collAddr)
        const loanUid = createMarketUid(chainId, lenderKey, debtAddr)
        const collMeta = metaMap[collUid]
        const loanMeta = metaMap[loanUid]
        const collDecimals = collMeta?.asset?.decimals ?? market.collDecimals
        const collDisplay = collMeta ? getDisplayPrice(collMeta) : 0
        const collOracle = collMeta ? getOraclePrice(collMeta) : 0
        const collHist = collMeta?.price?.priceUsd24h ?? collDisplay
        const loanDisplay = loanMeta ? getDisplayPrice(loanMeta) : 0
        const loanOracle = loanMeta ? getOraclePrice(loanMeta) : 0
        const loanHist = loanMeta?.price?.priceUsd24h ?? loanDisplay

        const lendingPositions: Record<string, any> = {}
        const modes: Record<string, number> = {}
        const hist: Record<
          string,
          { totalDeposits24h: number; totalDebt24h: number }
        > = {}

        for (const { cdp, ink, debt } of positions) {
          // Vat.ink is a wad regardless of gem-native decimals (the join
          // scales on entry), so parse at 18 and let the display side use
          // the market's decimals only for the token identity.
          const collStr = parseRawAmount(ink.toString(), 18)
          const collNum = Number(collStr)
          const debtStr = parseRawAmount(debt.toString(), debtDecimals)
          const debtNum = Number(debtStr)
          const dssInfo: DssPositionInfo = { ...cdp }

          lendingPositions[cdp.cdpId] = {
            [collUid]: {
              marketUid: collUid,
              underlying: collAddr,
              deposits: collStr,
              debt: '0',
              debtStable: '0',
              depositsUSD: collNum * collDisplay,
              debtUSD: 0,
              debtStableUSD: 0,
              depositsUSDOracle: collNum * collOracle,
              debtUSDOracle: 0,
              debtStableUSDOracle: 0,
              stableBorrowRate: '0',
              collateralEnabled: true,
              claimableRewards: 0,
            },
            [loanUid]: {
              marketUid: loanUid,
              underlying: debtAddr,
              deposits: '0',
              debt: debtStr,
              debtStable: '0',
              depositsUSD: 0,
              debtUSD: debtNum * loanDisplay,
              debtStableUSD: 0,
              depositsUSDOracle: 0,
              debtUSDOracle: debtNum * loanOracle,
              debtStableUSDOracle: 0,
              stableBorrowRate: '0',
              collateralEnabled: false,
              claimableRewards: 0,
              dssInfo,
            },
          }
          modes[cdp.cdpId] = 0
          hist[cdp.cdpId] = {
            totalDeposits24h: collNum * collHist,
            totalDebt24h: debtNum * loanHist,
          }
        }

        if (Object.keys(lendingPositions).length === 0) continue

        const userData = createMultiAccountTypeUserState(
          { chainId, account, lendingPositions, modes } as any,
          metaMap,
          hist,
        )
        if (userData && userData.data.length > 0) out[lenderKey] = userData
      }

      return Object.keys(out).length > 0 ? out : undefined
    },
    expected,
  ]
}
